import type { WidgetAppConfig } from '@pypestream/pype/customization';

/**
 * How a channel's saved Pype Next design answered, with the three outcomes told
 * apart: `absent` is the endpoint saying there is no saved design — a 200 with
 * `config: null` and nothing else — and `failed` is anything that stops us
 * knowing: a server error, a 404, a network error, a deadline, a body that is not
 * a design. A 404 is `failed` on purpose: the only 404 measured (candidate,
 * 2026-10-24) was an unknown app id, a gateway answers it too, and `absent` on a
 * target means "seed it and overwrite". Limitation: an environment that answers a
 * never-saved design with a 404 (UAT and production were not measured) cannot
 * seed such a target.
 *
 * `fetchWidgetAppDesign` in the widget package cannot be used for this: it is
 * non-throwing by contract and answers `null` for all of those, because the widget
 * must boot either way. For a copy that WRITES onto the target, "we could not
 * read it" and "there is nothing there" call for opposite actions.
 *
 * Limitation: the `{ config }` envelope rule below restates the one in
 * `fetch-widget-app-design.ts`; if the endpoint's answer changes shape, both change.
 */
export type DesignLoad =
  | { status: 'loaded'; config: WidgetAppConfig }
  | { status: 'absent' }
  | { status: 'failed' };

export interface LoadPypeNextDesignOptions {
  appId: string;
  engagementApiUrl: string;
  /** Defaults to 8 s: a copy waits for the answer, the widget's boot (1.5 s) does not. */
  timeoutMs?: number;
  fetchFn?: typeof fetch;
}

const DEFAULT_TIMEOUT_MS = 8000;
const FAILED: DesignLoad = { status: 'failed' };

export const loadPypeNextDesign = async ({
  appId,
  engagementApiUrl,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  fetchFn = fetch,
}: LoadPypeNextDesignOptions): Promise<DesignLoad> => {
  if (!appId || !engagementApiUrl) return FAILED;

  const ctrl = new AbortController();
  const timeoutId = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const url = `${engagementApiUrl.replace(/\/$/, '')}/widget/v1/apps/${encodeURIComponent(appId)}/design`;
    const res = await fetchFn(url, {
      signal: ctrl.signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return FAILED;

    const json = (await res.json()) as
      | WidgetAppConfig
      | { config?: WidgetAppConfig | null }
      | null;
    // A design that was never saved is answered 200 with `config: null` (measured
    // on the candidate engagement API, 2026-10-24); 404 is an unknown app id.
    if (
      json &&
      typeof json === 'object' &&
      'config' in json &&
      json.config === null
    ) {
      return { status: 'absent' };
    }
    const config =
      json && typeof json === 'object' && 'config' in json && json.config
        ? json.config
        : (json as WidgetAppConfig | null);
    if (!config?.design || !config.shell || !config.branding) return FAILED;
    return { status: 'loaded', config };
  } catch {
    return FAILED;
  } finally {
    clearTimeout(timeoutId);
  }
};
